import { Badge } from "@/components/ui/badge"; // Assuming you have a badge component
// apps/app/src/routes/Recordings/List/GroupRow.tsx
import { Heading } from "@/components/ui/heading";
import { Loading } from "@components";
import { InfiniteScrollSentinel } from "@components/InfiniteScrollSentinel";
import Panel from "@components/Panel";
import type { DiscoveryRow } from "@hvf/contracts";
import { userId } from "@state/auth";
import { type DiscoverySource, PAGE_SIZE, getRecordings } from "@state/recordings/api";
import { useInfiniteQuery } from "@tanstack/solid-query";
import { For, Show } from "solid-js";
import { Box, Flex, Grid, HStack } from "styled-system/jsx";
import Item from "./Item";

export default function GroupRow(props: { group: DiscoveryRow }) {
  const query = useInfiniteQuery(() => ({
    queryKey: ['recordings', userId(), props.group.source_type, props.group.filter_value],
    queryFn: ({ pageParam }) =>
      getRecordings(props.group.filter_value, props.group.source_type as DiscoverySource, pageParam as number),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loadedSoFar = allPages.length * PAGE_SIZE;
      return loadedSoFar < lastPage.count ? allPages.length : undefined;
    },
  }));

  const allRecordings = () => query.data?.pages.flatMap(p => p.data) ?? [];

  return (
    <Box display='grid'>
      <HStack justify="space-between" px='1'>
        <Heading>
          {props.group.group_name === 'General' ? 'Uncategorized' : props.group.group_name}
        </Heading>
        <Badge variant="outline" size="sm">
          {props.group.source_type === 'event' ? 'Event' : 'Recording'}
        </Badge>
      </HStack>

      <Flex overflowX="auto" flexWrap="nowrap" gap={3} alignItems="center" class="scrollbar" p='2'>
        <For each={allRecordings()}>
          {(recording) => <Item recording={recording} />}
        </For>

        <Show when={query.hasNextPage}>
          <InfiniteScrollSentinel onIntersect={() => query.fetchNextPage()} />
          <Show when={query.isFetchingNextPage}>
            <Box minW="50px" textAlign="center" position='relative'>
              <Loading />
            </Box>
          </Show>
        </Show>
      </Flex>
    </Box>
  );
}
